import { useCallback, useMemo } from "react";
import {
    useGetMenuItemsByCategoryEditPageQuery,
    useGetMenuItemsByChildCategoryEditPageQuery,
    GetMenuItemsByCategoryEditPageQuery,
    GetMenuItemsByChildCategoryEditPageQuery,
} from "@me-team/host/main/editMenuPage/graphql/editMenuPage.hooks";
import {
    MenuAllergenProxy,
    MenuItemPortionProxy,
    MenuMarkerProxy,
    MenuSupplementProxy,
} from "@me-team/host/main/graphql/types";
import { DeepPartial } from "@apollo/client/utilities";

export type DishCard = {
    id: number;
    name: string;
    nameOrigin?: string | null;
    unitName: string | null;
    allergens: DeepPartial<MenuAllergenProxy[]>;
    supplements: DeepPartial<MenuSupplementProxy[]>;
    description: string | null;
    descriptionOrigin?: string | null;
    favicon: string | null;
    markers: DeepPartial<MenuMarkerProxy[]>;
    composition: string | null;
    compositionOrigin?: string | null;
    portions: DeepPartial<MenuItemPortionProxy[]>;
    cookingTime: string | null;
};

export type ChildCategorySection = {
    id: number;
    name: string;
    nameOrigin?: string | null;
    dishes: DishCard[];
};

type Args = {
    currentCompanyId: string | number | null | undefined;
    categoryId: number | null | undefined;
    childCategoryId: number | null | undefined;
    menuId: number | null;
    localeVersion: string;
};

type CategoryQueryResult = ReturnType<typeof useGetMenuItemsByCategoryEditPageQuery>;

type Result = {
    loading: boolean;
    currencySign: string;
    dishes: DishCard[];
    childCategories: ChildCategorySection[];
    source: "category" | "childCategory";
    refetch: () => ReturnType<CategoryQueryResult["refetch"]>;
};

function mapDish(item: any): DishCard {
    return {
        id: Number(item?.id),
        name: item?.name ?? "",
        nameOrigin: item?.nameOrigin ?? null,
        unitName: item?.unit?.name ?? null,
        allergens: item?.allergens ?? [],
        supplements: item?.supplements ?? [],
        description: item?.description ?? null,
        descriptionOrigin: item?.descriptionOrigin ?? null,
        favicon: item?.favicon ?? null,
        markers: item?.markers ?? [],
        composition: item?.composition ?? null,
        compositionOrigin: item?.compositionOrigin ?? null,
        portions: item?.portions ?? [],
        cookingTime: item?.cookingTime ?? null,
    };
}

function extractActive(
    hasChild: boolean,
    catData: GetMenuItemsByCategoryEditPageQuery | undefined,
    childData: GetMenuItemsByChildCategoryEditPageQuery | undefined
) {
    if (hasChild) {
        const company = childData?.user?.company;
        const childCategory = company?.meMenuCategories?.[0]?.childCategories?.[0];

        return {
            currencySign: company?.currency?.currencySign ?? "",
            menuItems: childCategory?.menuItems ?? [],
            childCategories: [],
        };
    }

    const company = catData?.user?.company;
    const category = company?.meMenuCategories?.[0];

    return {
        currencySign: company?.currency?.currencySign ?? "",
        menuItems: category?.menuItems ?? [],
        childCategories:
            category?.childCategories?.map((childCategory) => ({
                id: Number(childCategory?.id),
                name: childCategory?.name ?? "",
                nameOrigin: childCategory?.nameOrigin ?? null,
                dishes: (childCategory?.menuItems ?? []).map(mapDish),
            })) ?? [],
    };
}

const useMenuItemsEditPage = (args: Args): Result => {
    const companyId = args.currentCompanyId ? Number(args.currentCompanyId) : null;
    const categoryId = args.categoryId ?? null;
    const childCategoryId = args.childCategoryId ?? null;
    const menuId = args.menuId ?? null;

    const hasBase = !!companyId && !!categoryId;
    const hasChild = childCategoryId != null;

    const canFetchByCategory = hasBase && !hasChild;
    const canFetchByChild = hasBase && hasChild;

    const {
        data: categoryData,
        loading: categoryLoading,
        refetch: refetchCategory,
    } = useGetMenuItemsByCategoryEditPageQuery({
        skip: !canFetchByCategory || !args.localeVersion,
        variables: {
            companyId: companyId ?? 0,
            categoryId,
            menuId,
            locale: args.localeVersion,
            isDeletedSupplements: false,
        },
        fetchPolicy: "network-only",
        context: { errorType: "global" },
    });

    const {
        data: childData,
        loading: childLoading,
        refetch: refetchChild,
    } = useGetMenuItemsByChildCategoryEditPageQuery({
        skip: !canFetchByChild || !args.localeVersion,
        variables: {
            companyId: companyId ?? 0,
            categoryId,
            childCategoryId: childCategoryId ?? 0,
            menuId,
            locale: args.localeVersion,
        },
        fetchPolicy: "network-only",
        context: { errorType: "global" },
    });

    const loading = categoryLoading || childLoading;

    const active = useMemo(() => {
        const extracted = extractActive(hasChild, categoryData, childData);
        const activeRefetch = hasChild ? refetchChild : refetchCategory;

        return {
            ...extracted,
            refetch: activeRefetch,
        };
    }, [hasChild, categoryData, childData, refetchCategory, refetchChild]);

    const refetch = useCallback(() => active.refetch(), [active]);

    const dishes = useMemo<DishCard[]>(() => {
        return active.menuItems.map(mapDish);
    }, [active.menuItems]);

    return {
        loading,
        currencySign: active.currencySign,
        dishes,
        childCategories: active.childCategories,
        source: hasChild ? "childCategory" : "category",
        refetch,
    };
};

export default useMenuItemsEditPage;
